import {
  U3129_HONORARIUM_NO_PROJECT_KEY,
  type U3129HonorariumLineRef,
  type U3129HonorariumProjectGroup,
  u3129HonorariumProjectHeaderLabels,
} from './u3129GroupHonorariumForDisplay';
import { u3129ProjectWorkNatureFromRow } from './u3129HonorariumWorkNatureDisplay';
import type { U3129HonorariumItem } from '../U3129Service';

export type U3129HonorariumSortOrder = 'ASC' | 'DESC';

export type U3129HonorariumProjectSortField = 'default' | 'project' | 'customer' | 'natures';

function honorariumTaskSortLabel(row: U3129HonorariumItem): string {
  const desc = row.task?.description?.trim();
  if (desc) {
    return desc;
  }
  return (row.taskType ?? '').trim();
}

function compareStrings(a: string, b: string, order: U3129HonorariumSortOrder): number {
  const c = a.localeCompare(b, undefined, { sensitivity: 'base' });
  return order === 'ASC' ? c : -c;
}

function projectGroupSortKey(group: U3129HonorariumProjectGroup, field: U3129HonorariumProjectSortField): string {
  if (group.projectKey === U3129_HONORARIUM_NO_PROJECT_KEY) {
    return '\uffff';
  }
  const sample = group.sampleRow;
  if (field === 'customer') {
    return u3129HonorariumProjectHeaderLabels(sample).clientLabel.trim();
  }
  if (field === 'natures') {
    return u3129ProjectWorkNatureFromRow(sample);
  }
  if (field === 'project') {
    return u3129HonorariumProjectHeaderLabels(sample).projectLabel.trim();
  }
  return u3129HonorariumProjectHeaderLabels(sample).projectLabel.trim();
}

/** Tri groupes projet — `default` = ordre BL-0674 (libellé projet ASC). */
export function u3129SortHonorariumProjectGroups(
  groups: U3129HonorariumProjectGroup[],
  field: U3129HonorariumProjectSortField,
  order: U3129HonorariumSortOrder,
): U3129HonorariumProjectGroup[] {
  if (field === 'default') {
    return [...groups];
  }
  const effectiveOrder = order;
  return [...groups].sort((a, b) => {
    if (a.projectKey === U3129_HONORARIUM_NO_PROJECT_KEY) return 1;
    if (b.projectKey === U3129_HONORARIUM_NO_PROJECT_KEY) return -1;
    const ka = projectGroupSortKey(a, field);
    const kb = projectGroupSortKey(b, field);
    const c = compareStrings(ka, kb, effectiveOrder);
    if (c !== 0) {
      return c;
    }
    return (a.projectId ?? '').localeCompare(b.projectId ?? '');
  });
}

export function u3129SortHonorariumLineRefsByTask(
  lineRefs: U3129HonorariumLineRef[],
  order: U3129HonorariumSortOrder,
): U3129HonorariumLineRef[] {
  return [...lineRefs].sort((a, b) => {
    const c = compareStrings(honorariumTaskSortLabel(a.row), honorariumTaskSortLabel(b.row), order);
    if (c !== 0) {
      return c;
    }
    return order === 'ASC' ? a.index - b.index : b.index - a.index;
  });
}

/** Réordonne les lignes tâche dans chaque sous-groupe du projet ouvert. */
export function u3129ApplyTaskSortToProjectGroup(
  group: U3129HonorariumProjectGroup,
  order: U3129HonorariumSortOrder,
): U3129HonorariumProjectGroup {
  return {
    ...group,
    subprojectGroups: group.subprojectGroups.map((sg) => ({
      ...sg,
      lines: u3129SortHonorariumLineRefsByTask(sg.lines, order),
    })),
  };
}
